import ContractAttachment from '@/components/ContractAttachment'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    NEGOTIATION_PRIORITY,
    NEGOTIATION_STATUS,
} from '@/modules/Customer/constants'
import { getNegotiationHistory } from '@/modules/Customer/services'
import { ContractNegotiation } from '@/modules/Customer/types'
import { formatDateWithFormatter, uniqBy } from '@/utils/function'
import { paths } from '@/utils/path'
import { Archive } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from 'vnetwork-ui'
import { Color } from 'vnetwork-ui/dist/types'

const Archived: React.FC<{ negotiation: ContractNegotiation }> = ({
    negotiation,
}) => {
    const { t } = useTranslation()

    const { data } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.NEGOTIATIONS,
                'history',
                negotiation.contract_negotiation_id,
            ],
            queryFn: () =>
                getNegotiationHistory(negotiation.contract_negotiation_id),
        },
        { showLoading: false, showError: false }
    )

    const statusObj = NEGOTIATION_STATUS(t).find(
        (s) => s.value === negotiation.contract_negotiation_status
    )
    const priorityObj = NEGOTIATION_PRIORITY(t).find(
        (p) => p.value === negotiation.contract_negotiation_priority
    )

    return (
        <div className="mb-4">
            <SubHeaders
                breadcrumbs={[
                    {
                        title: t('negotiations.list'),
                        path: paths.contractNegotiation,
                    },
                    { title: negotiation.contract_negotiation_code, path: '' },
                ]}
            />

            {/* Archived banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2 flex items-center gap-3">
                <Archive className="h-4 w-4 text-amber-600 shrink-0" />
                <div className="flex items-center gap-2 text-sm text-amber-800">
                    <span className="font-medium">
                        {t('archived-negotiation')}
                    </span>
                    <span className="text-xs text-amber-600">
                        {formatDateWithFormatter(negotiation.archived_at)}
                    </span>
                </div>
            </div>

            <Paper className="p-4 mt-2">
                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                    <span className="text-base font-semibold text-gray-500">
                        {negotiation.contract_negotiation_title}
                    </span>
                    {statusObj && (
                        <Chip
                            label={statusObj.label}
                            color={statusObj.color as Color}
                            size="small"
                        />
                    )}
                    {priorityObj && (
                        <Chip
                            label={priorityObj.label}
                            color={priorityObj.color as Color}
                            size="small"
                        />
                    )}
                </div>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>
                        {t('negotiations.creator')}:{' '}
                        <strong className="text-gray-700">
                            {negotiation.creator_name}
                        </strong>
                    </span>
                    <span>
                        {t('negotiations.created_at')}:{' '}
                        {formatDateWithFormatter(negotiation.created_at)}
                    </span>
                </div>
            </Paper>

            {/* Attachments from history */}
            {data?.results?.some((h: any) => h.attachments?.length > 0) && (
                <Paper className="mt-2">
                    <ContractAttachment
                        attachments={
                            uniqBy(
                                data.results.flatMap(
                                    (h: any) => h.attachments || []
                                ),
                                'attachment_id'
                            ) || []
                        }
                    />
                </Paper>
            )}
        </div>
    )
}

export default Archived
